import React, { useState } from 'react';
import {
  Fab,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  alpha,
  useTheme
} from '@mui/material';
import {
  RocketLaunch,
  Business,
  Group,
  AutoAwesome,
  Payment,
  RestartAlt,
  BugReport
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface OnboardingTriggerProps {
  onTrigger?: (startStep?: number) => void;
}

export const OnboardingTrigger: React.FC<OnboardingTriggerProps> = ({ onTrigger }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Only show in development mode
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const triggerOnboarding = (step?: number) => {
    handleClose();
    
    // Store fake user data in localStorage for dev mode
    const devUser = {
      id: 999,
      email: 'dev@safee.fr',
      role: 'MODERATOR',
      hasBeenOnboarded: false,
      entrepriseId: null,
      fullname: 'Dev User'
    };
    
    localStorage.setItem('auth', JSON.stringify({
      user: devUser,
      token: 'dev-token-123',
      refreshToken: 'dev-refresh-123'
    }));

    // Store the starting step if provided
    if (step !== undefined) {
      sessionStorage.setItem('onboarding_start_step', step.toString());
    }

    // Navigate to dashboard which will trigger onboarding
    if (window.location.pathname === '/dashboard') {
      // If already on dashboard, reload to trigger onboarding
      window.location.reload();
    } else {
      navigate('/dashboard');
    }
  };

  const steps = [
    { icon: Business, label: 'Création entreprise', step: 0 },
    { icon: Group, label: 'Invitation équipe', step: 1 },
    { icon: AutoAwesome, label: 'Démo features', step: 2 },
    { icon: Payment, label: 'Paywall', step: 3 }
  ];

  return (
    <>
      <Fab
        color="primary"
        aria-label="dev-onboarding"
        onClick={handleClick}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 9998,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          '&:hover': {
            transform: 'scale(1.1)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`
          }
        }}
      >
        <BugReport />
      </Fab>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: -2,
            minWidth: 280,
            background: alpha(theme.palette.background.paper, 0.98),
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" color="primary" fontWeight={600}>
            🚀 Dev Tools - Onboarding B2B
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Déclencher l'onboarding à n'importe quelle étape
          </Typography>
        </Box>
        
        <Divider />

        <MenuItem onClick={() => triggerOnboarding(0)}>
          <ListItemIcon>
            <RocketLaunch color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary="Démarrer depuis le début"
            secondary="Écran de bienvenue + toutes les étapes"
          />
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ px: 2, py: 0.5, display: 'block' }}
        >
          Démarrer à une étape spécifique :
        </Typography>

        {steps.map((step, index) => (
          <MenuItem key={index} onClick={() => triggerOnboarding(step.step)}>
            <ListItemIcon>
              <step.icon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary={step.label}
              secondary={`Étape ${step.step + 1}`}
            />
          </MenuItem>
        ))}

        <Divider sx={{ my: 1 }} />

        <MenuItem onClick={() => {
          localStorage.removeItem('auth');
          sessionStorage.removeItem('onboarding_start_step');
          handleClose();
          window.location.reload();
        }}>
          <ListItemIcon>
            <RestartAlt color="error" />
          </ListItemIcon>
          <ListItemText 
            primary="Reset auth & onboarding"
            secondary="Nettoyer les données de dev"
          />
        </MenuItem>

        <Box 
          sx={{ 
            px: 2, 
            py: 1, 
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            borderTop: `1px solid ${theme.palette.divider}`
          }}
        >
          <Typography variant="caption" color="warning.main">
            ⚠️ Mode développement uniquement
          </Typography>
        </Box>
      </Menu>
    </>
  );
};